// pages/FormViewerPage/services/formStaticAllowlist.ts — FASE 0 form statis.
// Aturan keras: yang dibakar ke HTML statis HARUS himpunan bagian dari data
// yang SPA publik boleh ambil hari ini (FORM_PUBLIC_COLUMNS + products
// name/attributes + brand publik). Kolom BARU hanya boleh masuk lewat
// FORM_PUBLIC_COLUMNS (ada test yang menjaga), TIDAK PERNAH via `*`.
// Pengecualian terdokumentasi: biteshipSettings.apiKey ikut karena SPA hari ini
// juga menerimanya (rates key publik, jalur legacy) — bukan penambahan vektor.
import { FORM_PUBLIC_COLUMNS } from '../../../utils/cacheHelpers/forms';

const PUBLIC_KEYS = new Set(
  FORM_PUBLIC_COLUMNS.split(',').map((s) => s.trim().replace(/^"|"$/g, '')),
);

// Jaring pengaman kedua: walau suatu hari sebuah kunci secret lolos ke
// FORM_PUBLIC_COLUMNS, bakar tetap menolaknya di sini.
// ASIMETRI SENGAJA vs bootstrap /api/form-public (routes/formPublic.js
// KEEP_SECRET_PATHS mempertahankan biteshipSettings.apiKey): HTML statis
// di-cache edge (nginx public s-maxage + SWR) → secret DILARANG dibakar.
// Bootstrap same-origin per-request boleh membawa apiKey legacy itu; klien
// mengambilnya pasca-hidrasi via fetchBiteshipApiKey bila perlu.
const FORBIDDEN_RE = /api[_-]?key|secret|token|password|private|salt|signature|webhook|costprice|margin|supplier|hpp|modal|profit/i;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function stripForbiddenDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripForbiddenDeep);
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    if (FORBIDDEN_RE.test(k)) continue;
    out[k] = stripForbiddenDeep(v);
  }
  return out;
}

/** Salin form hanya dengan kolom publik + buang kunci sensitif (rekursif). */
export function sanitizeFormForStatic(form: unknown): Record<string, unknown> {
  if (!isPlainObject(form)) return {};
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(form)) {
    if (!PUBLIC_KEYS.has(k)) continue;
    if (FORBIDDEN_RE.test(k)) continue;
    out[k] = stripForbiddenDeep((form as Record<string, unknown>)[k]);
  }
  return out;
}

/** Produk: HANYA nama + atribut (mirror fetchProductData). Harga/stok/CPP
 *  TIDAK ikut — harga final selalu revalidasi via API saat island hidup. */
export function sanitizeProductForStatic(
  p: unknown,
): { name: unknown; attributes: unknown } | null {
  if (!isPlainObject(p)) return null;
  return { name: (p as any).name ?? null, attributes: (p as any).attributes ?? null };
}

const BRAND_PUBLIC_KEYS = new Set(['id', 'name', 'custom_domain', 'logo']);

/** Brand: identitas + logo footer saja. */
export function sanitizeBrandForStatic(b: unknown): Record<string, unknown> {
  if (!isPlainObject(b)) return {};
  const out: Record<string, unknown> = {};
  for (const k of BRAND_PUBLIC_KEYS) {
    if ((b as Record<string, unknown>)[k] !== undefined) out[k] = (b as Record<string, unknown>)[k];
  }
  return out;
}

/**
 * Bentuk akhir data baked yang disuntik ke HTML statis DAN dibaca klien
 * saat boot (window.__FORM_DATA__). WAJIB dipakai di kedua sisi agar render
 * pertama identik: sanitize + buang productImages (mirror `cleanForm` di
 * effectsForm — gambar produk tidak ikut payload data).
 * Return null bila tak layak jadi baked (fallback: fetch normal).
 */
export function prepareBakedForm(raw: unknown): Record<string, unknown> | null {
  const clean = sanitizeFormForStatic(raw);
  if (!clean || typeof clean.slug !== 'string' || !clean.slug) return null;
  const { productImages: _drop, ...rest } = clean;
  void _drop;
  return rest;
}

/** Baca baked form dari window (klien). Cocokkan slug case-insensitive. */
export function readBakedForm(identifier: string): Record<string, unknown> | null {
  try {
    if (typeof window === 'undefined') return null;
    const w = window as any;
    const baked = w.__FORM_DATA__;
    const bootSlug = String(w.__FORM_BOOT_SLUG || '').toLowerCase();
    if (!baked || typeof baked !== 'object') return null;
    if (!identifier || bootSlug !== String(identifier).toLowerCase()) return null;
    const clean = prepareBakedForm(baked);
    if (!clean || String(clean.slug || '').toLowerCase() !== String(identifier).toLowerCase()) return null;
    return clean;
  } catch {
    return null;
  }
}
